// =============================================================
//  ManageUsersScreen — Gestion utilisateurs / centres / admins
//  Onglets segmentés + recherche + table + actions.
// =============================================================

import React, { useEffect, useMemo, useState } from 'react';
import { View, Text, StyleSheet, useWindowDimensions } from 'react-native';
import { collection, query, getDocs, orderBy, where } from 'firebase/firestore';
import { db } from '../private/firebaseConfig';

import AdminLayout from './web-ui/AdminLayout';
import {
  Card,
  Button,
  Badge,
  EmptyState,
  Loading,
  Avatar,
  DataRow,
  SegmentedSelect,
  SearchBar,
  StatCard,
} from './web-ui/kit';
import { colors, spacing } from './web-ui/theme';

const TABS = [
  { value: 'users', label: 'Utilisateurs' },
  { value: 'centers', label: 'Centres' },
  { value: 'admins', label: 'Administrateurs' },
];

const TAB_TO_COLLECTION = {
  users: { col: 'users', filter: ['userType', '==', 'user'] },
  centers: { col: 'centers' },
  admins: { col: 'admin' },
};

export default function ManageUsersScreen({ navigation }) {
  const { width } = useWindowDimensions();
  const isCompact = width < 800;

  const [activeTab, setActiveTab] = useState('users');
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [loading, setLoading] = useState(true);
  const [items, setItems] = useState([]);
  const [counts, setCounts] = useState({ total: 0, active: 0, blocked: 0 });

  const loadData = async (tab) => {
    setLoading(true);
    try {
      const cfg = TAB_TO_COLLECTION[tab];
      const ref = collection(db, cfg.col);
      let q;
      if (cfg.filter) {
        q = query(ref, where(...cfg.filter), orderBy('createdAt', 'desc'));
      } else {
        q = query(ref, orderBy('createdAt', 'desc'));
      }
      const snap = await getDocs(q);
      const data = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
      const active = data.filter((u) => u.status === 'active' || !u.status).length;
      const blocked = data.filter((u) => u.status === 'blocked').length;
      setCounts({ total: data.length, active, blocked });
      setItems(data);
    } catch (e) {
      console.log('loadData error', e?.message || e);
      setItems([]);
      setCounts({ total: 0, active: 0, blocked: 0 });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData(activeTab);
  }, [activeTab]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return items.filter((u) => {
      if (statusFilter !== 'all') {
        const isBlocked = u.status === 'blocked';
        if (statusFilter === 'active' && isBlocked) return false;
        if (statusFilter === 'blocked' && !isBlocked) return false;
      }
      if (!q) return true;
      const name = (u.fullName || u.centerName || u.name || '').toLowerCase();
      const email = (u.email || '').toLowerCase();
      return name.includes(q) || email.includes(q);
    });
  }, [items, search, statusFilter]);

  const goToDetail = (item) => {
    if (activeTab === 'centers') {
      navigation.navigate('CenterDetailScreen', { center: item });
    } else {
      navigation.navigate('UserDetailScreen', { user: item, userType: activeTab });
    }
  };

  return (
    <AdminLayout
      title="Gestion des utilisateurs"
      subtitle="Utilisateurs, centres et administrateurs de la plateforme"
    >
      <View style={styles.statsRow}>
        <StatCard
          label={`Total ${TABS.find((t) => t.value === activeTab)?.label}`}
          value={counts.total}
          icon="account-group"
          accent={colors.primary}
          loading={loading}
        />
        <StatCard
          label="Actifs"
          value={counts.active}
          icon="check-circle-outline"
          accent={colors.success}
          loading={loading}
        />
        <StatCard
          label="Bloqués"
          value={counts.blocked}
          icon="cancel"
          accent={colors.danger}
          loading={loading}
        />
      </View>

      <Card style={{ marginBottom: spacing.lg }}>
        <SegmentedSelect options={TABS} value={activeTab} onChange={setActiveTab} />
      </Card>

      <View
        style={[
          styles.toolbar,
          isCompact && { flexDirection: 'column', alignItems: 'stretch' },
        ]}
      >
        <View style={{ flex: 1, minWidth: 240 }}>
          <SearchBar
            value={search}
            onChangeText={setSearch}
            placeholder="Rechercher par nom ou email..."
          />
        </View>
        <SegmentedSelect
          value={statusFilter}
          onChange={setStatusFilter}
          options={[
            { value: 'all', label: 'Tous' },
            { value: 'active', label: 'Actifs' },
            { value: 'blocked', label: 'Bloqués' },
          ]}
        />
      </View>

      <Card noPadding>
        {loading ? (
          <Loading label="Chargement..." />
        ) : filtered.length === 0 ? (
          <EmptyState
            icon="account-search-outline"
            title="Aucun résultat"
            subtitle={search ? 'Essaie une autre recherche' : 'Aucun élément dans cette catégorie'}
          />
        ) : (
          filtered.map((u, i) => {
            const name = u.fullName || u.centerName || u.name || 'Sans nom';
            const isBlocked = u.status === 'blocked';
            return (
              <DataRow
                key={u.id}
                last={i === filtered.length - 1}
                onPress={() => goToDetail(u)}
              >
                <Avatar src={u.photoURL || u.imageUrl} name={name} size={40} />
                <View style={{ flex: 1, minWidth: 0 }}>
                  <Text style={styles.rowTitle} numberOfLines={1}>
                    {name}
                  </Text>
                  <Text style={styles.rowSubtitle} numberOfLines={1}>
                    {u.email || u.phoneNumber || '—'}
                  </Text>
                </View>
                {!isCompact && (
                  <Badge variant={isBlocked ? 'danger' : 'success'}>
                    {isBlocked ? 'Bloqué' : 'Actif'}
                  </Badge>
                )}
                {activeTab === 'admins' && u.adminLevel && (
                  <Badge variant="primary">{u.adminLevel}</Badge>
                )}
                <Button size="sm" variant="ghost" icon="arrow-right" onPress={() => goToDetail(u)} />
              </DataRow>
            );
          })
        )}
      </Card>
    </AdminLayout>
  );
}

const styles = StyleSheet.create({
  statsRow: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: spacing.lg,
    marginBottom: spacing.lg,
  },
  toolbar: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.md,
    marginBottom: spacing.lg,
  },
  rowTitle: { fontSize: 14, fontWeight: '600', color: colors.text },
  rowSubtitle: { fontSize: 12, color: colors.textSecondary, marginTop: 2 },
});
